import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  setLoading(isLoading) {
    const { addToCartButton } = this.refs;
    addToCartButton.classList.toggle('atc-loading', isLoading);
    if (isLoading) addToCartButton.setAttribute('aria-busy', 'true');
    else addToCartButton.removeAttribute('aria-busy');
  }

  showSuccess() {
    this.animateAddToCart();
    if (!this.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLButtonElement | undefined} buyNowButton - The buy now button.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  #isSubmitting = false;

  #variantIsUpdating = false;

  #canAddToCart = false;

  #isBuyNowSubmission = false;

  /** @type {Promise<void>} */
  #variantUpdatePromise = Promise.resolve();

  /** @type {(() => void) | undefined} */
  #resolveVariantUpdate;

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    this.#canAddToCart = !this.refs.addToCartButtonContainer?.refs.addToCartButton?.disabled;
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    event.preventDefault();

    if (this.#isSubmitting) return;

    const form = this.querySelector('form');
    const submitter = 'submitter' in event ? event.submitter : null;
    const shouldCheckout = submitter instanceof HTMLElement && submitter.hasAttribute('data-buy-now');

    if (!(form instanceof HTMLFormElement)) {
      this.#showError('No pudimos iniciar la compra. Recarga la página e intenta nuevamente.');
      return;
    }

    if (submitter instanceof HTMLButtonElement && submitter.disabled) return;
    let isRedirecting = false;
    let customerErrorMessage = 'No pudimos agregar el producto. Intenta nuevamente.';

    this.#isSubmitting = true;
    this.#isBuyNowSubmission = shouldCheckout;
    this.#hideError();
    this.#setSubmittingState(true);
    this.#setLiveRegionText('Agregando…');

    try {
      await this.#waitForVariantUpdate();

      if (!this.#canAddToCart) {
        customerErrorMessage = 'Selecciona una presentación disponible antes de continuar.';
        throw new Error('The selected variant is unavailable');
      }

      const formData = new FormData(form);
      const variantId = String(formData.get('id') ?? '').trim();

      if (!/^\d+$/.test(variantId)) {
        customerErrorMessage = 'Selecciona una presentación disponible antes de continuar.';
        throw new Error('A valid variant ID is required');
      }

      formData.delete('return_to');

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const cartItemComponentsSectionIds = new Set();
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          cartItemComponentsSectionIds.add(item.dataset.sectionId);
        }
      });
      if (!shouldCheckout && cartItemComponentsSectionIds.size > 0) {
        formData.set('sections', Array.from(cartItemComponentsSectionIds).join(','));
      }

      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'application/json',
        },
      });
      const responseData = await response.json();

      if (!response.ok || responseData.status) {
        customerErrorMessage = responseData.description || responseData.message || customerErrorMessage;
        throw new Error('Shopify rejected the cart addition');
      }

      if (!shouldCheckout) this.refs.addToCartButtonContainer?.showSuccess();
      this.#setLiveRegionText(shouldCheckout ? 'Producto agregado. Abriendo el checkout.' : Theme.translations.added);

      if (shouldCheckout) {
        isRedirecting = true;
        window.location.assign('/checkout');
        return;
      }

      this.dispatchEvent(
        new CartAddEvent(responseData, variantId, {
          source: 'product-form-component',
          itemCount: Number(formData.get('quantity')) || 1,
          productId: this.dataset.productId,
          variantId,
          sections: responseData.sections,
        })
      );

      const hasRequestedSections =
        cartItemComponentsSectionIds.size === 0 ||
        Array.from(cartItemComponentsSectionIds).some((sectionId) => responseData.sections?.[sectionId]);
      if (!hasRequestedSections) {
        isRedirecting = true;
        window.location.assign(Theme.routes.cart_url);
      }
    } catch (error) {
      this.#showError(customerErrorMessage);
      window.dispatchEvent(new CartErrorEvent(this.id, customerErrorMessage));
      console.error(error);
    } finally {
      cartPerformance.measureFromEvent('add:user-action', event);
      if (!isRedirecting) {
        this.#isSubmitting = false;
        this.#setSubmittingState(false);
        this.#isBuyNowSubmission = false;
      }
    }
  }

  #setSubmittingState(isSubmitting) {
    if (isSubmitting) this.setAttribute('aria-busy', 'true');
    else this.removeAttribute('aria-busy');
    this.refs.addToCartButtonContainer?.setLoading(isSubmitting && !this.#isBuyNowSubmission);
    const addToCartButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    const buyNowButton = this.refs.buyNowButton;
    if (addToCartButton) addToCartButton.disabled = isSubmitting || !this.#canAddToCart;
    if (buyNowButton) {
      buyNowButton.disabled = isSubmitting || !this.#canAddToCart;
      buyNowButton.toggleAttribute('aria-busy', isSubmitting && this.#isBuyNowSubmission);
      if (isSubmitting && this.#isBuyNowSubmission) {
        if (buyNowButton.dataset.originalText == null) {
          buyNowButton.dataset.originalText = buyNowButton.textContent ?? '';
        }
        buyNowButton.textContent = 'Agregando…';
      } else if (buyNowButton.dataset.originalText != null) {
        buyNowButton.textContent = buyNowButton.dataset.originalText;
        delete buyNowButton.dataset.originalText;
      }
    }
    this.#setVariantControlsDisabled(isSubmitting);
  }

  #setVariantControlsDisabled(isDisabled) {
    const scope = this.closest('.product-details, [data-product-grid-content], product-card, dialog, .shopify-section');
    if (!scope) return;

    scope.querySelectorAll('variant-picker input, variant-picker select').forEach((control) => {
      if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) return;
      if (isDisabled) {
        if (control.dataset.purchaseFlowWasDisabled == null) {
          control.dataset.purchaseFlowWasDisabled = control.disabled.toString();
        }
        control.disabled = true;
      } else {
        if (control.dataset.purchaseFlowWasDisabled == null) return;
        control.disabled = control.dataset.purchaseFlowWasDisabled === 'true';
        delete control.dataset.purchaseFlowWasDisabled;
      }
    });
  }

  #showError(message) {
    const { addToCartTextError } = this.refs;
    if (addToCartTextError) {
      const messageElement = addToCartTextError.querySelector('.product-form-text__error-message');
      if (messageElement) messageElement.textContent = message;
      else addToCartTextError.append(document.createTextNode(message));
      addToCartTextError.classList.remove('hidden');
    }
    this.#setLiveRegionText(message);
  }

  #hideError() {
    this.refs.addToCartTextError?.classList.add('hidden');
    this.#clearLiveRegionText();
  }

  async #waitForVariantUpdate() {
    while (this.#variantIsUpdating) {
      await this.#variantUpdatePromise;
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  #onVariantSelected = (event) => {
    const source = event.target;
    if (!(source instanceof HTMLElement) || source.dataset.productId !== this.dataset.productId) return;
    this.#resolveVariantUpdate?.();
    this.#variantIsUpdating = true;
    this.#variantUpdatePromise = new Promise((resolve) => {
      this.#resolveVariantUpdate = resolve;
    });
  };

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    this.#variantIsUpdating = false;
    this.#resolveVariantUpdate?.();
    this.#resolveVariantUpdate = undefined;
    variantId.value = event.detail.resource?.id ?? '';
    this.#canAddToCart = Boolean(event.detail.resource?.available);

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButton?.setAttribute('disabled', 'true');
      this.refs.buyNowButton?.setAttribute('disabled', 'true');
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      currentAddToCartButton?.removeAttribute('disabled');
      this.refs.buyNowButton?.removeAttribute('disabled');
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (currentAddToCartButton && newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (this.#isSubmitting) this.#setSubmittingState(true);

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
